import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Check, ChevronRight, ChevronLeft, Info } from 'lucide-react';
import SectionGuide from '../../components/SectionGuide';
import { ProgressIndicator } from './ProgressIndicator';
import { IMPLEMENTATION_TYPES } from './constants';

export function WizardStep2({
  step, animKey, stepAnimClass, guideResetKey,
  implementationType, setImplementationType,
  goToStep, setShowTypeInfo
}) {
  return (
    <div key={`step2-${animKey}`} className={stepAnimClass} data-testid="wizard-step-2">
      <SectionGuide id="estimator-impl-type" title="Implementation Type" accent="#3B82F6" resetKey={guideResetKey}>
        <p>
          Your <strong>implementation type</strong> determines which components are relevant and how effort scales. <em>Corporate LMS</em> focuses on compliance and admin training; <em>EXE/Ecommerce</em> adds storefront and payment configuration; <em>Workforce Enablement</em> includes field-training and operational assessment features.
        </p>
        <p>
          This choice sets the default component weighting — high-priority items for your type are pre-selected, while less-common ones are still available if needed.
        </p>
      </SectionGuide>
      <ProgressIndicator step={step} goToStep={goToStep} />
      
      <div className="text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
          What type of LMS implementation?
        </h2>
        <p className="text-lg text-slate-500 max-w-xl mx-auto">
          Select the primary use case for your learning platform
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
        {IMPLEMENTATION_TYPES.map((type) => (
          <Card 
            key={type.id}
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border ${
              implementationType === type.id 
                ? `${type.borderColor} ${type.bgColor} ring-2 ring-[#0F2040] border-2` 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setImplementationType(type.id)}
            data-testid={`impl-type-${type.id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 rounded-xl ${type.iconBg} flex items-center justify-center`}>
                  <type.icon className={`w-7 h-7 ${type.iconColor}`} />
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTypeInfo(type); }}
                  className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  data-testid={`impl-type-info-${type.id}`}
                >
                  <Info className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
              <h3 className="text-xl font-semibold text-[#0F2040] mb-2">{type.title}</h3>
              <p className="text-slate-600 text-sm mb-4">{type.desc}</p>
              
              <div className="space-y-2">
                {type.highlights.map((h) => (
                  <div key={h} className="flex items-center gap-2 text-xs text-slate-500">
                    <Check className="w-3 h-3 text-green-500" />
                    {h}
                  </div>
                ))}
              </div>
              
              {implementationType === type.id && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-[#0F2040]">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Selected</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-between max-w-5xl mx-auto">
        <Button variant="outline" onClick={() => goToStep(1)} data-testid="step2-back-btn">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={() => goToStep(3)}
          disabled={!implementationType}
          className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white"
          data-testid="step2-continue-btn"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
